import { RenderThreejs } from '@jscadui/render-threejs'

// Track loaded scripts to avoid reloading
const loadedScripts = new Set()

// Track current resize observer to disconnect on engine switch
let currentResizeObserver = null

/**
 * Initialize the 3D viewer with the specified render engine
 * @param {'threejs' | 'regl'} engineType
 * @returns {Promise<object>} The viewer instance
 */
export const init = async (engineType = 'threejs') => {
  const el = /** @type {HTMLDivElement} */ (document.getElementById('viewer'))

  // Disconnect previous resize observer to avoid stale callbacks
  if (currentResizeObserver) {
    currentResizeObserver.disconnect()
    currentResizeObserver = null
  }

  let viewer

  if (engineType === 'regl') {
    // Load regl library and render-regl bundle
    await addScript('build/bundle.regl.js')
    await addScript('build/bundle.render-regl.js')
    // @ts-expect-error - RenderReglBundle is loaded as a global
    const JscadReglViewer = RenderReglBundle.RenderRegl()
    viewer = JscadReglViewer(el)
    // Regl computes flat normals in fragment shader via dFdx/dFdy
    viewer.supportsGpuNormals = true
  } else {
    // Default to Three.js
    await addScript('build/bundle.threejs.js')
    const JscadThreeViewer = RenderThreejs(THREE)
    viewer = JscadThreeViewer(el)
    // Meshes without normals get a flatShading material, which takes the face normal from screen-space derivatives
    viewer.supportsGpuNormals = true
  }

  // Set up resize observer for future resizes
  observeResize(el, evt => viewer.resize(evt.contentRect))

  // Trigger initial resize with current element dimensions
  const rect = el.getBoundingClientRect()
  viewer.resize({ width: rect.width, height: rect.height })

  return viewer
}

/**
 * @param {HTMLElement} el
 * @param {(entry:ResizeObserverEntry)=>void} listener
 */
const observeResize = (el, listener) => {
  // ResizeObserver is better than window resize as it can be used on any element
  // Store in module-level variable so we can disconnect on engine switch
  currentResizeObserver = new ResizeObserver(entries => {
    listener(entries[0])
  })
  currentResizeObserver.observe(el)
}

/**
 * Load a script dynamically by adding a <script> tag to the document.
 *
 * SECURITY NOTE (FALSE POSITIVE): This function loads scripts from hardcoded
 * build paths (e.g., 'build/bundle.threejs.js'). The source URLs are NOT
 * user-controlled - they are determined by the engineType parameter which
 * only accepts 'threejs' or 'regl' values.
 *
 * @param {string} source
 * @param {boolean} [module]
 * @returns {Promise<void>}
 */
const addScript = async (source, module = false) => {
  // Don't reload already-loaded scripts
  if (loadedScripts.has(source)) return

  return new Promise((resolve, reject) => {
    const tag = document.createElement('script')
    tag.type = module ? 'module' : 'text/javascript'
    tag.src = source
    tag.onload = () => {
      loadedScripts.add(source)
      resolve()
    }
    tag.onerror = err => reject(err)
    document.head.append(tag)
  })
}
